import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, X, Plus } from "lucide-react";
import UploadImage from "@/components/UploadImage";
import { useAuth } from "@/auth/AuthContext";

export default function Profile() {
  const { setVendor, refresh } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [tag, setTag] = useState("");
  const [saving, setSaving] = useState(false);

  // Two-step account deletion state
  const [deleteStep, setDeleteStep] = useState(null); // null | "confirm" | "password"
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/vendors/me");
        if (!alive) return;
        setForm({
          business_name: data.business_name || "",
          description: data.description || "",
          cuisine_tags: data.cuisine_tags || [],
          email: data.email || "",
          phone: data.phone || "",
          logo_url: data.logo_url || "",
          social_instagram: data.social_instagram || "",
          social_facebook: data.social_facebook || "",
          social_tiktok: data.social_tiktok || "",
          social_twitter: data.social_twitter || "",
        });
      } catch (err) {
        console.error("[Profile] load failed:", err?.message || err);
        toast.error("Failed to load profile");
      }
    })();
    return () => { alive = false; };
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addTag = () => {
    const t = tag.trim();
    if (!t) return;
    if (form.cuisine_tags.includes(t)) return;
    update("cuisine_tags", [...form.cuisine_tags, t]);
    setTag("");
  };

  const removeTag = (t) => update("cuisine_tags", form.cuisine_tags.filter((x) => x !== t));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/vendors/me", form);
      setVendor(data);
      toast.success("Profile saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const closeDeleteFlow = () => {
    if (deleting) return;
    setDeleteStep(null);
    setDeletePassword("");
    setDeleteError("");
  };

  const confirmDelete = async (e) => {
    e.preventDefault();
    if (!deletePassword) {
      setDeleteError("Please enter your password.");
      return;
    }
    setDeleting(true);
    setDeleteError("");
    try {
      await api.post("/auth/delete-account", { password: deletePassword });
      // Backend already cleared httpOnly auth cookies in the delete response.
      // Just sync local auth state (which will become null since cookies are gone), then redirect.
      await refresh();
      toast.success("Account deleted");
      navigate("/", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        setDeleteError("Incorrect password. Please try again.");
      } else {
        setDeleteError(err?.response?.data?.detail || "Failed to delete account. Please try again.");
      }
      setDeleting(false);
    }
  };

  if (!form) return <div className="text-slate-500 text-sm">Loading…</div>;

  return (
    <>
      <form onSubmit={save} className="max-w-3xl space-y-6" data-testid="profile-form">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Profile</h1>
          <p className="text-slate-500 mt-1 text-sm">Tell hungry customers who you are.</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <div>
            <Label>Logo</Label>
            <div className="mt-2">
              <UploadImage value={form.logo_url} onChange={(v) => update("logo_url", v)} testid="profile-logo" />
            </div>
          </div>

          <div>
            <Label htmlFor="business_name">Business name</Label>
            <Input
              id="business_name"
              value={form.business_name}
              onChange={(e) => update("business_name", e.target.value)}
              required
              data-testid="profile-business-name"
            />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              rows={4}
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              placeholder="What makes your truck special?"
              data-testid="profile-description"
            />
          </div>

          <div>
            <Label>Cuisine tags</Label>
            <div className="mt-2 flex flex-wrap gap-2" data-testid="profile-cuisine-tags">
              {form.cuisine_tags.map((t) => (
                <span key={t} className="inline-flex items-center gap-1 bg-[#FDECE2] text-[#E8650A] text-xs font-medium px-2 py-1 rounded-full">
                  {t}
                  <button type="button" onClick={() => removeTag(t)} data-testid={`remove-tag-${t}`}>
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
            <div className="mt-2 flex gap-2">
              <Input
                value={tag}
                onChange={(e) => setTag(e.target.value)}
                placeholder="e.g. Tacos"
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                data-testid="profile-tag-input"
              />
              <Button type="button" variant="outline" onClick={addTag} data-testid="profile-tag-add">
                <Plus size={14} className="mr-1" /> Add
              </Button>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <h2 className="text-base font-semibold">Contact</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="email">Public email</Label>
              <Input id="email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="profile-email" />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="profile-phone" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <h2 className="text-base font-semibold">Socials</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="ig">Instagram</Label>
              <Input id="ig" value={form.social_instagram} onChange={(e) => update("social_instagram", e.target.value)} placeholder="@handle" data-testid="profile-instagram" />
            </div>
            <div>
              <Label htmlFor="fb">Facebook</Label>
              <Input id="fb" value={form.social_facebook} onChange={(e) => update("social_facebook", e.target.value)} data-testid="profile-facebook" />
            </div>
            <div>
              <Label htmlFor="tt">TikTok</Label>
              <Input id="tt" value={form.social_tiktok} onChange={(e) => update("social_tiktok", e.target.value)} data-testid="profile-tiktok" />
            </div>
            <div>
              <Label htmlFor="tw">Twitter / X</Label>
              <Input id="tw" value={form.social_twitter} onChange={(e) => update("social_twitter", e.target.value)} data-testid="profile-twitter" />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => setDeleteStep("confirm")}
            className="border-red-500 text-red-600 hover:bg-red-50 hover:text-red-700"
            data-testid="profile-delete-account-btn"
          >
            Delete Account
          </Button>
          <Button type="submit" className="ff-btn-primary" disabled={saving} data-testid="profile-save">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save profile
          </Button>
        </div>
      </form>

      {/* Step 1 — Confirmation */}
      <Dialog open={deleteStep === "confirm"} onOpenChange={(o) => { if (!o) closeDeleteFlow(); }}>
        <DialogContent className="sm:max-w-md" data-testid="delete-confirm-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold tracking-tight">
              Delete <span className="text-red-600">Account</span>?
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete your account? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={closeDeleteFlow}
              data-testid="delete-keep-btn"
            >
              No, keep my account
            </Button>
            <Button
              type="button"
              onClick={() => { setDeleteStep("password"); setDeleteError(""); setDeletePassword(""); }}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-confirm-yes-btn"
            >
              Yes, delete it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Step 2 — Password verification */}
      <Dialog open={deleteStep === "password"} onOpenChange={(o) => { if (!o) closeDeleteFlow(); }}>
        <DialogContent className="sm:max-w-md" data-testid="delete-password-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold tracking-tight">
              Confirm your <span className="text-red-600">identity</span>
            </DialogTitle>
            <DialogDescription>
              Please enter your password to permanently delete your account and all associated data.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={confirmDelete} className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label htmlFor="delete-password">Password</Label>
              <Input
                id="delete-password"
                type="password"
                value={deletePassword}
                onChange={(e) => { setDeletePassword(e.target.value); if (deleteError) setDeleteError(""); }}
                placeholder="Enter your password"
                autoFocus
                required
                data-testid="delete-password-input"
              />
              {deleteError && (
                <p className="text-sm text-red-600" data-testid="delete-password-error">
                  {deleteError}
                </p>
              )}
            </div>
            <DialogFooter className="gap-2 sm:gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={closeDeleteFlow}
                disabled={deleting}
                data-testid="delete-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="delete-submit-btn"
              >
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete my account"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
